/**
 * 船機設定檔的維護（`config/` 三個檔 + `data/` 的 ASR 詞表）。
 *
 * ## 三種樣子
 *
 * ATTRACTION_* 兩個檔由景點資料產生，這一頁只顯示產生結果（唯讀）；要手改就按
 * 「改為手動覆寫」，此時文字框會先帶入產生結果，改哪裡一眼看得出來。
 *
 * asr_keywords_dynamic.json 的 `content` 是**底稿**不是覆寫值，所以產生模式下
 * 文字框照樣可以編輯（見 `content_role`）；產生結果放在另一塊「產生結果」區，預設
 * 收合——那是一份十萬字元起跳的 JSON，一律攤開只會讓頁面變慢，而多數時候使用者
 * 是來貼底稿的。但**一定要看得到**：底稿裡永遠不會出現新景點的 `roi_N`（產生器是
 * 抽換 `subCategories`，不是往底稿裡加），只給一行「N 行、X KB」的話，「新景點有沒有
 * 進去」這個問題在畫面上無法回答，而那正是最常被問的問題。
 *
 * CHECK_CONFIG.TXT 沒有產生來源（它的參數不是景點資料，0811 版規格書也沒再提到
 * 這個檔），維持整份文字貼上、逐位元原樣送上船。
 *
 * 格式與警告一律由後端算：發布時用的是同一份邏輯，前端再實作一次就會漂移。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../components/Button';
import {
  CONFIG_FILE_HINT,
  listDisplayConfigFiles,
  updateDisplayConfigFile,
  type ConfigFilename,
  type ConfigSource,
  type DisplayConfigFile,
} from '../lib/api-client';

export function DisplayConfigFilesPage(): JSX.Element {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['display.config-files'],
    queryFn: listDisplayConfigFiles,
  });

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">船機設定檔</h1>
      </div>

      <div
        style={{
          background: '#fffbeb',
          border: '1px solid #fcd34d',
          borderRadius: 6,
          padding: 12,
          margin: '12px 0',
          fontSize: 13,
          maxWidth: 900,
        }}
      >
        兩個 <code>ATTRACTION_*</code> 檔由<strong>景點資料自動產生</strong>
        （已發布且已設定窗屏編號的景點），改景點名稱或座標就會跟著變，不需要手動維護。
        <code>asr_keywords_dynamic.json</code> 的店家清單由各景點的推薦點產生，
        <strong>底稿</strong>（全域分類與控制項的諧音字）請貼上廠商提供的整份 JSON。
        <code>CHECK_CONFIG.TXT</code> 沒有產生來源，請貼上廠商提供的內容。
        <br />
        改錯會影響整台船機的行為（GPS 埠、觸發距離、景點座標、語音辨識），不是單一景點顯示不對。
      </div>

      {query.isLoading && <div>載入中…</div>}
      {query.error && <div className="error-banner">{(query.error as Error).message}</div>}

      {query.data?.map((file) => (
        <ConfigFileEditor
          key={file.filename}
          file={file}
          onSaved={() => void qc.invalidateQueries({ queryKey: ['display.config-files'] })}
        />
      ))}
    </div>
  );
}

function ConfigFileEditor({
  file,
  onSaved,
}: {
  file: DisplayConfigFile;
  onSaved: () => void;
}): JSX.Element {
  const [draft, setDraft] = useState(file.content);
  const [lineEnding, setLineEnding] = useState(file.line_ending);

  // 伺服器版本變了（他人編輯、重新載入）就跟上——沒有這段的話存檔後畫面仍是舊值
  useEffect(() => {
    setDraft(file.content);
    setLineEnding(file.line_ending);
  }, [file.content, file.line_ending]);

  const save = useMutation({
    mutationFn: () =>
      updateDisplayConfigFile(file.filename as ConfigFilename, {
        content: draft,
        line_ending: lineEnding as 'crlf' | 'lf',
      }),
    onSuccess: onSaved,
  });

  const switchSource = useMutation({
    mutationFn: (source: ConfigSource) =>
      updateDisplayConfigFile(file.filename as ConfigFilename, {
        source,
        // 切成手動時把產生結果帶進去當起點：要手改的多半是「產生的內容再補一筆」，
        // 從空白開始只會讓人回頭去別的地方複製一份
        ...(source === 'manual' && file.content.trim() === '' && file.generated
          ? { content: file.generated }
          : {}),
      }),
    onSuccess: onSaved,
  });

  const saveLineEnding = useMutation({
    mutationFn: (le: 'crlf' | 'lf') =>
      updateDisplayConfigFile(file.filename as ConfigFilename, { line_ending: le }),
    onSuccess: onSaved,
  });

  const generated = file.source === 'generated';
  /*
   * 產生模式下文字框顯示什麼，看 `content` 在這個檔裡的角色：
   *   override（ATTRACTION_*）→ 顯示產生結果，唯讀，content 用不到
   *   base（ASR 詞表）→ 顯示底稿，**仍可編輯**，產生結果放在下方可展開的區塊
   * 若一律顯示產生結果，貼底稿的人會找不到地方貼。
   */
  const isBase = file.content_role === 'base';
  const readOnly = generated && !isBase;
  const shown = readOnly ? (file.generated ?? '') : draft;

  // 貼上的內容可能是 CRLF，而伺服器存的是 LF——比較前先正規化，否則每次載入
  // 都會顯示成「未儲存」
  const dirty =
    !readOnly && (draft.replace(/\r\n/g, '\n') !== file.content || lineEnding !== file.line_ending);

  return (
    <section
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 6,
        padding: 16,
        margin: '12px 0',
        maxWidth: 900,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 15, fontFamily: 'monospace' }}>{file.filename}</h2>
        <span style={{ fontSize: 12, color: '#9ca3af' }}>
          → 船上{' '}
          <code>
            {isBase ? 'data' : 'config'}/{file.filename}
          </code>
        </span>
        <span style={{ flex: 1 }} />
        {file.generatable && (
          <span
            style={{
              fontSize: 12,
              padding: '2px 8px',
              borderRadius: 10,
              background: generated ? '#ecfdf5' : '#fef3c7',
              color: generated ? '#047857' : '#92400e',
              border: `1px solid ${generated ? '#a7f3d0' : '#fcd34d'}`,
            }}
          >
            {generated ? '由景點資料產生' : '手動覆寫中'}
          </span>
        )}
        {dirty && <span style={{ fontSize: 12, color: '#b45309' }}>● 未儲存</span>}
      </div>

      <p style={{ fontSize: 12, color: '#6b7280', margin: '6px 0 8px' }}>
        {CONFIG_FILE_HINT[file.filename as ConfigFilename]}
      </p>

      {file.warnings.map((w) => (
        <div
          key={w}
          style={{
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: 4,
            padding: 8,
            fontSize: 12,
            marginBottom: 8,
          }}
        >
          ⚠ {w}
        </div>
      ))}

      {isBase && (
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
          底稿（廠商提供的整份 JSON）：
        </div>
      )}

      <textarea
        value={shown}
        onChange={(e) => setDraft(e.target.value)}
        readOnly={readOnly}
        rows={10}
        spellCheck={false}
        placeholder={
          isBase
            ? '貼上廠商提供的 asr_keywords_dynamic.json 整份內容⋯（留空代表此檔不納入發布包）'
            : '貼上整份檔案內容⋯（留空代表此檔不納入發布包）'
        }
        style={{
          width: '100%',
          padding: 8,
          fontFamily: 'Consolas, Menlo, monospace',
          fontSize: 13,
          border: '1px solid #d1d5db',
          borderRadius: 4,
          resize: 'vertical',
          whiteSpace: 'pre',
          overflowWrap: 'normal',
          overflowX: 'auto',
          background: readOnly ? '#f9fafb' : '#fff',
          color: readOnly ? '#374151' : 'inherit',
        }}
      />

      {!readOnly && file.generated !== null && <GeneratedPanel file={file} shipping={generated} />}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
        {!readOnly && (
          <Button
            variant="primary"
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
          >
            {save.isPending ? '儲存中…' : isBase && generated ? '儲存底稿' : '儲存'}
          </Button>
        )}
        {generated && (
          <>
            <Button
              variant="ghost"
              onClick={() => switchSource.mutate('manual')}
              disabled={switchSource.isPending}
            >
              改為手動覆寫
            </Button>
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              {isBase ? '店家清單跟著推薦點資料走' : '內容跟著景點資料走；要手改再切換'}
            </span>
          </>
        )}
        {dirty && (
          <Button
            variant="ghost"
            onClick={() => {
              setDraft(file.content);
              setLineEnding(file.line_ending);
            }}
          >
            還原
          </Button>
        )}
        {file.generatable && !generated && (
          <Button
            variant="ghost"
            onClick={() => switchSource.mutate('generated')}
            disabled={switchSource.isPending}
          >
            改回自動產生
          </Button>
        )}
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
          行尾
          <select
            value={lineEnding}
            onChange={(e) => {
              const next = e.target.value as 'crlf' | 'lf';
              setLineEnding(next);
              // 產生模式沒有「儲存」鈕（內容不是這裡編的），但行尾仍要能改——直接送出
              if (generated) saveLineEnding.mutate(next);
            }}
            style={{ padding: 4, fontSize: 12 }}
          >
            <option value="crlf">CRLF（Windows）</option>
            <option value="lf">LF</option>
          </select>
        </label>
        <span style={{ fontSize: 12, color: '#9ca3af' }}>
          {file.line_count} 行{shown.trim() === '' && '　（留空 → 不寫進發布包）'}
        </span>
      </div>

      {[save.error, switchSource.error, saveLineEnding.error].map(
        (err) =>
          err && (
            <div key={String(err)} className="error-banner">
              {(err as Error).message}
            </div>
          ),
      )}
    </section>
  );
}

/**
 * 產生結果：預設收合，展開才渲染。
 *
 * 只在「文字框顯示的不是產生結果」時出現——ATTRACTION_* 在自動模式下文字框本身
 * 就是產生結果，再給一份是多餘的。會用到的是兩種情況：ASR 詞表（文字框是底稿），
 * 以及手動覆寫中的 ATTRACTION_*（想比對產生器會給什麼）。
 */
function GeneratedPanel({
  file,
  shipping,
}: {
  file: DisplayConfigFile;
  /** 這份產生結果是不是真的會送上船（source=generated）。 */
  shipping: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const text = file.generated ?? '';

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 4,
        marginTop: 8,
        background: '#f9fafb',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '6px 8px',
          fontSize: 12,
          flexWrap: 'wrap',
        }}
      >
        <strong style={{ color: shipping ? '#047857' : '#92400e' }}>產生結果</strong>
        <span style={{ color: '#6b7280' }}>
          {file.line_count} 行、{Math.round(text.length / 1024)} KB
          {shipping
            ? '　（這份就是會送上船的內容）'
            : '　（目前送上船的是上面的手動內容，這只供比對）'}
        </span>
        <span style={{ flex: 1 }} />
        <Button variant="ghost" onClick={() => setOpen((v) => !v)}>
          {open ? '收合' : '檢視產出'}
        </Button>
        <Button variant="ghost" onClick={() => downloadText(file.filename, text, file.line_ending)}>
          下載
        </Button>
      </div>

      {open && (
        <textarea
          value={text}
          readOnly
          rows={16}
          spellCheck={false}
          style={{
            width: '100%',
            padding: 8,
            fontFamily: 'Consolas, Menlo, monospace',
            fontSize: 12,
            border: 'none',
            borderTop: '1px solid #e5e7eb',
            borderRadius: 0,
            resize: 'vertical',
            whiteSpace: 'pre',
            overflowWrap: 'normal',
            overflowX: 'auto',
            background: '#fff',
            color: '#374151',
          }}
        />
      )}
    </div>
  );
}

/**
 * 下載成檔案。
 *
 * 行尾照 `line_ending` 展開，讓下載到的檔案與發布包裡那一份**逐位元相同**（同
 * `display-config-files.service` 的 `buildFiles`）——不然拿去跟船上的檔比對時，
 * 每一行都會不一樣。
 */
function downloadText(filename: string, text: string, lineEnding: string): void {
  const body = lineEnding === 'crlf' ? text.replace(/\n/g, '\r\n') : text;
  const url = URL.createObjectURL(new Blob([body], { type: 'text/plain;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
